/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Customer Studio templates", description = "Reuse audience conditions, sync configuration, and journey structures across Customer Studio.", lastUpdatedDate = new Date(1788134400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Customer Studio templates",
        description: "Reuse audience conditions, sync configuration, and journey structures across Customer Studio.",
        lastUpdatedDate: "Mon Aug 31 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How templates fit into Customer Studio",
          "depth": 0,
          "id": "how-templates-fit-into-customer-studio"
        }, {
          "value": "What changes after you apply a template",
          "depth": 1,
          "id": "what-changes-after-you-apply-a-template"
        }, {
          "value": "Templates and traits",
          "depth": 1,
          "id": "templates-and-traits"
        }, {
          "value": "Audience templates",
          "depth": 0,
          "id": "audience-templates"
        }, {
          "value": "Create an audience template",
          "depth": 1,
          "id": "create-an-audience-template"
        }, {
          "value": "Apply an audience template",
          "depth": 1,
          "id": "apply-an-audience-template"
        }, {
          "value": "Check a template before applying it",
          "depth": 1,
          "id": "check-a-template-before-applying-it"
        }, {
          "value": "Sync templates",
          "depth": 0,
          "id": "sync-templates"
        }, {
          "value": "Create a sync template",
          "depth": 1,
          "id": "create-a-sync-template"
        }, {
          "value": "Apply a sync template during audience creation",
          "depth": 1,
          "id": "apply-a-sync-template-during-audience-creation"
        }, {
          "value": "Apply a sync template to an existing audience",
          "depth": 1,
          "id": "apply-a-sync-template-to-an-existing-audience"
        }, {
          "value": "Apply a sync template in a journey",
          "depth": 1,
          "id": "apply-a-sync-template-in-a-journey"
        }, {
          "value": "How changes to sync templates propagate",
          "depth": 1,
          "id": "how-changes-to-sync-templates-propagate"
        }, {
          "value": "Require sync templates for audience activation",
          "depth": 1,
          "id": "require-sync-templates-for-audience-activation"
        }, {
          "value": "Make sync template fields editable",
          "depth": 1,
          "id": "make-sync-template-fields-editable"
        }, {
          "value": "Journey templates",
          "depth": 0,
          "id": "journey-templates"
        }, {
          "value": "Create a journey template",
          "depth": 1,
          "id": "create-a-journey-template"
        }, {
          "value": "Apply a journey template",
          "depth": 1,
          "id": "apply-a-journey-template"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      a: "a",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      ol: "ol",
      img: "img"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Platform admins, marketers"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "An audience, sync configuration, or journey you want to reuse."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Templates save reusable audience, sync, and journey configurations so your team can apply the same setup without rebuilding it each time."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-templates-fit-into-customer-studio",
        children: "How templates fit into Customer Studio"
      }), "\n", _jsx(_components.p, {
        children: "Use templates when your team repeatedly builds the same targeting pattern, destination setup, or campaign structure. A template gives users a configured starting point while letting them fill in the details that change from one campaign to the next. For example, you might reuse the same churn-risk audience with a different inactivity window, or start every welcome campaign from the same journey structure."
      }), "\n", _jsx(_components.p, {
        children: "The type of template determines what gets reused:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "When you want to reuse..."
            }), _jsx(_components.th, {
              children: "Use"
            }), _jsx(_components.th, {
              children: "Example"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "A targeting pattern"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#audience-templates",
                children: _jsx(_components.strong, {
                  children: "Audience template"
                })
              })
            }), _jsx(_components.td, {
              children: "Customers at risk of churn"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Destination setup and field mappings"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#sync-templates",
                children: _jsx(_components.strong, {
                  children: "Sync template"
                })
              })
            }), _jsx(_components.td, {
              children: "Standard Braze audience sync"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "A multi-step campaign structure"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#journey-templates",
                children: _jsx(_components.strong, {
                  children: "Journey template"
                })
              })
            }), _jsx(_components.td, {
              children: "New-customer welcome series"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "A template is a starting point, not a saved audience or a running campaign. When someone uses a template, they create a new audience, sync, or journey from its configuration."
      }), "\n", _jsx(_components.p, {
        children: "Platform admins typically create and govern templates, and marketers apply them when building campaigns."
      }), "\n", _jsx(_components.h3, {
        id: "what-changes-after-you-apply-a-template",
        children: "What changes after you apply a template"
      }), "\n", _jsx(_components.p, {
        children: "How a template behaves after you apply it depends on its type:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience and journey templates"
          }), " give a new object its starting configuration. Once the object is created, it's independent of the template."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Syncs created from a sync template"
          }), " stay linked to it, so supported changes to the template propagate to those syncs. See ", _jsx(_components.a, {
            href: "#how-changes-to-sync-templates-propagate",
            children: "How changes to sync templates propagate"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "templates-and-traits",
        children: "Templates and traits"
      }), "\n", _jsxs(_components.p, {
        children: ["Templates and ", _jsx(_components.a, {
          href: "/customer-studio/traits",
          children: "traits"
        }), " solve different reuse problems:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Traits"
          }), " reuse a calculated customer value, such as ", _jsx(_components.code, {
            children: "90-day purchase value"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Templates"
          }), " reuse configuration, such as audience conditions, destination mappings, or journey structure."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can use them together. An audience template can reference a saved trait, so teams reuse both the calculation and the targeting pattern built from it."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "audience-templates",
        children: "Audience templates"
      }), "\n", _jsx(_components.p, {
        children: "Use an audience template when marketers repeatedly build audiences from the same targeting pattern but need to customize some conditions for each campaign. For example, you might create a churn-risk template and leave the inactivity window blank so marketers choose the right number of days when they apply it."
      }), "\n", _jsx(_components.h3, {
        id: "create-an-audience-template",
        children: "Create an audience template"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to ", _jsxs(_components.strong, {
              children: ["Customer Studio > Templates > ", _jsx(_components.a, {
                href: "https://app.hightouch.com/templates/audience-templates?from=docs",
                children: "Audience templates"
              })]
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Add audience template"
            }), ".\n", _jsx(_components.img, {
              src: "hightouch-audiences/templates/cs-temp-add-aud.png",
              alt: "Add audience templates"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select a parent model.\n", _jsx(_components.img, {
              src: "hightouch-audiences/templates/cs-temp-aud-parent-model.png",
              alt: "Choose a parent model"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Add the filters, traits, and events you want the template to include.\n", _jsx(_components.img, {
              src: "hightouch-audiences/templates/cs-temp-define-filters.png",
              alt: "Define the audience template"
            })]
          }), "\n", _jsx(_components.p, {
            children: "Leave fields blank when marketers should provide the value each time they use the template."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Name the template and optionally add a description."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Finish"
            }), ".\n", _jsx(_components.img, {
              src: "hightouch-audiences/templates/cs-temp-finish-aud.png",
              alt: "Finish audience"
            })]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To create a template from an existing audience, open the audience, select the\n", _jsx(_components.strong, {
            children: "..."
          }), " menu, and select ", _jsx(_components.strong, {
            children: "Create template from this audience"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "apply-an-audience-template",
        children: "Apply an audience template"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Go to ", _jsxs(_components.strong, {
              children: ["Customer Studio > ", _jsx(_components.a, {
                href: "https://app.hightouch.com/audiences?from=docs",
                children: "Audiences"
              })]
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Add audience"
            }), " and select a parent model."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Open the ", _jsx(_components.strong, {
              children: "..."
            }), " menu and select ", _jsx(_components.strong, {
              children: "Add a template"
            }), ", or select a template under ", _jsx(_components.strong, {
              children: "Start with an audience template"
            }), ".\n", _jsx(_components.img, {
              src: "hightouch-audiences/templates/cs-temp-apply-aud.png",
              alt: "Apply audience template"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Select a template with a matching parent model."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Fill in any required blanks, highlighted in blue."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Save the audience and continue editing as needed."
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can apply more than one template to the same audience. Hightouch adds the conditions from each template to the audience builder, where you can review and edit the combined filters before you save."
      }), "\n", _jsx(_components.h3, {
        id: "check-a-template-before-applying-it",
        children: "Check a template before applying it"
      }), "\n", _jsx(_components.p, {
        children: "Open a template from the list to preview it before anyone builds from it. From the template editor, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Calculate size"
          }), " to see how many members the template resolves to."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Preview a destination"
          }), " to check the rows the template would send."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "View SQL"
          }), " from the ", _jsx(_components.strong, {
            children: "..."
          }), " menu to inspect the generated query."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "sync-templates",
        children: "Sync templates"
      }), "\n", _jsx(_components.p, {
        children: "Sync templates reuse destination setup across audience and journey syncs. Configure the destination, record matching, and field mappings once, then apply that setup wherever your team needs the same sync configuration. You can apply multiple sync templates to one audience to create syncs to several destinations."
      }), "\n", _jsx(_components.h3, {
        id: "create-a-sync-template",
        children: "Create a sync template"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsxs(_components.strong, {
            children: ["Customer Studio > Templates > ", _jsx(_components.a, {
              href: "https://app.hightouch.com/templates/sync-templates?from=docs",
              children: "Sync templates"
            })]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add sync template"
          }), ".\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-add-sync.png",
            alt: "Add sync template"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a parent model. Only audiences built on the same parent model can use the template.\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-sync-parent-model.png",
            alt: "Select a parent model"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a destination and configure the sync settings. Follow the ", _jsx(_components.a, {
            href: "/destinations/overview",
            children: "destination configuration instructions"
          }), " for your destination.\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-sync-destination.png",
            alt: "Select a destination"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can now use the sync template for multiple audience and journey syncs."
      }), "\n", _jsx(_components.h3, {
        id: "apply-a-sync-template-during-audience-creation",
        children: "Apply a sync template during audience creation"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Build a new audience and proceed through ", _jsx(_components.strong, {
            children: "Select parent model"
          }), ", ", _jsx(_components.strong, {
            children: "Define audience"
          }), ", and ", _jsx(_components.strong, {
            children: "Configure audience details"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Sync to destination"
          }), " step, select one or more matching sync templates."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Finish"
          }), ", or select ", _jsx(_components.strong, {
            children: "Save audience without syncs"
          }), " to skip for now."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/templates/cs-temp-apply-sync-creation.png",
          alt: "Sync an audience to a destination using a template"
        })
      }), "\n", _jsx(_components.h3, {
        id: "apply-a-sync-template-to-an-existing-audience",
        children: "Apply a sync template to an existing audience"
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "An audience can only use sync templates derived from the same parent model as\nthe audience."
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsxs(_components.strong, {
            children: ["Customer Studio > ", _jsx(_components.a, {
              href: "https://app.hightouch.com/audiences?from=docs",
              children: "Audiences"
            })]
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select an audience."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add sync"
          }), ".\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-add-sync-existing.png",
            alt: "Add sync to an audience"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Create syncs from templates"
          }), ", select one or more sync templates."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add syncs"
          }), ".\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-create-syncs-modal.png",
            alt: "Create syncs from templates"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The sync inherits configuration and scheduling from the template. See ", _jsx(_components.a, {
          href: "#how-changes-to-sync-templates-propagate",
          children: "How changes to sync templates propagate"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "apply-a-sync-template-in-a-journey",
        children: "Apply a sync template in a journey"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Build your journey and add a ", _jsx(_components.strong, {
            children: "Send to destination"
          }), " tile to the canvas."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose a destination. Sync templates appear in the list with a ", _jsx(_components.strong, {
            children: "Template"
          }), " badge.\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-journey-select-template.png",
            alt: "Select a sync template in a journey"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Journey settings"
          }), ", under ", _jsx(_components.strong, {
            children: "What type of sync is this?"
          }), ", select ", _jsx(_components.strong, {
            children: "Trigger"
          }), " or ", _jsx(_components.strong, {
            children: "Cohort"
          }), ", and choose when to remove users.\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-journey-settings.png",
            alt: "Configure journey settings for the sync"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Configure sync"
          }), ", fill in any fields the template left blank and edit any editable fields.\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-journey-configure-sync.png",
            alt: "Configure the sync from the template"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Finalize"
          }), ", add an optional description, then select ", _jsx(_components.strong, {
            children: "Save"
          }), ".\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-journey-finalize.png",
            alt: "Finalize the journey sync"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Save the journey."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The sync inherits configuration from the template. See ", _jsx(_components.a, {
          href: "#how-changes-to-sync-templates-propagate",
          children: "How changes to sync templates propagate"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "how-changes-to-sync-templates-propagate",
        children: "How changes to sync templates propagate"
      }), "\n", _jsx(_components.p, {
        children: "Syncs created from a sync template stay linked to it. When you change a locked field on the template, Hightouch applies that change to every sync derived from it. Editable fields keep the values set on each sync, so local customizations are preserved."
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "What type of sync is this?"
        }), " setting (Trigger or Cohort) is an exception. Changes to that setting don't propagate to journey syncs, so update those syncs individually."]
      }), "\n", _jsx(_components.h3, {
        id: "require-sync-templates-for-audience-activation",
        children: "Require sync templates for audience activation"
      }), "\n", _jsx(_components.p, {
        children: "Workspace admins can require marketers to use a sync template whenever they add a sync to an audience. Use this setting when every audience sync should follow approved record matching, field mappings, or consent handling."
      }), "\n", _jsx(_components.p, {
        children: "To enable it:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Settings > Workspace > Workspace"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Turn on ", _jsx(_components.strong, {
            children: "Require sync templates for audience activation"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/templates/require-sync-templates.png",
          alt: "Require sync templates for audience activation"
        })
      }), "\n", _jsx(_components.p, {
        children: "When this setting is on:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Users can only add syncs to audiences using sync templates."
        }), "\n", _jsx(_components.li, {
          children: "Users can't detach audience syncs from their templates."
        }), "\n", _jsx(_components.li, {
          children: "If no sync templates exist in the workspace, users can't add audience syncs until you create at least one."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "make-sync-template-fields-editable",
        children: "Make sync template fields editable"
      }), "\n", _jsx(_components.p, {
        children: "A field that isn't editable stays locked: it keeps the template's value and propagates template changes. Make a field editable when you want users to customize it per sync."
      }), "\n", _jsxs(_components.p, {
        children: ["Which fields you can make editable depends on the destination and its sync mode. Editable fields are available for audience-sync destinations, primarily ad platforms and messaging tools. Open the sync template configuration to see which fields a destination exposes, check the ", _jsx(_components.a, {
          href: "/destinations/overview",
          children: "destination's documentation"
        }), ", or ", _jsx(PylonLink, {
          children: "contact us"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To make a field editable, open the sync template configuration and turn on ", _jsx(_components.strong, {
          children: "Editable"
        }), " for that field."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/templates/cs-temp-editable-toggle.png",
          alt: "Editable toggle on a sync template field"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After the template is attached to an audience, users can override its editable fields when they configure the sync. A ", _jsx(_components.strong, {
          children: "Custom value"
        }), " badge marks any field they change, and ", _jsx(_components.strong, {
          children: "Restore default"
        }), " reverts it to the template's value. Users can update these overrides at any time from the sync configuration.\n", _jsx(_components.img, {
          src: "hightouch-audiences/sync/assign-overrides.png",
          alt: "Override an editable field with Custom value and Restore default"
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "journey-templates",
        children: "Journey templates"
      }), "\n", _jsx(_components.p, {
        children: "Use a journey template to give teams a reusable starting structure for common lifecycle campaigns, such as welcome, nurture, or re-engagement flows. When a marketer creates a journey from the template, Hightouch creates a new journey with the template's structure already in place. The marketer then configures it for the specific campaign."
      }), "\n", _jsx(_components.h3, {
        id: "create-a-journey-template",
        children: "Create a journey template"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsxs(_components.strong, {
            children: ["Customer Studio > Templates > ", _jsx(_components.a, {
              href: "https://app.hightouch.com/templates/journey-templates?from=docs",
              children: "Journey templates"
            })]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create journey template"
          }), ".\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-add-journey.png",
            alt: "Journey templates list"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Select a parent model."
        }), "\n", _jsx(_components.li, {
          children: "Enter a template name and optionally add a description."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create template"
          }), ".\n", _jsx(_components.img, {
            src: "hightouch-audiences/templates/cs-temp-add-journey-2.png",
            alt: "Add a journey template"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Build the journey structure in the template editor. Any ", _jsx(_components.a, {
          href: "/customer-studio/journeys/create#add-journey-notes",
          children: "journey notes"
        }), " you add carry over to journeys created from the template."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To create a journey template from an existing journey, open the journey,\nselect the ", _jsx(_components.strong, {
            children: "..."
          }), " menu, and select ", _jsx(_components.strong, {
            children: "Create template from this journey"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "apply-a-journey-template",
        children: "Apply a journey template"
      }), "\n", _jsx(_components.p, {
        children: "Create a journey from a template to start with its structure instead of a blank canvas."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsxs(_components.strong, {
            children: ["Customer Studio > Templates > ", _jsx(_components.a, {
              href: "https://app.hightouch.com/templates/journey-templates?from=docs",
              children: "Journey templates"
            })]
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "..."
          }), " menu on a template and select ", _jsx(_components.strong, {
            children: "Create journey from this template"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Create journey"
          }), " dialog, enter a journey name and optional description. The parent model comes from the template."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create journey"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch creates a new journey from the template's structure and opens it in the journey builder."
      }), "\n", _jsxs(_components.p, {
        children: ["You can also apply a template to a journey you're already editing: in the journey builder, open ", _jsx(_components.strong, {
          children: "Templates"
        }), " from the ", _jsx(_components.strong, {
          children: "Add to canvas"
        }), " panel and choose a template. Applying it replaces the tiles on the canvas with the template's structure."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "Build an audience"
          }), " to create audiences you can save and reuse as templates."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/syncs",
            children: "Sync audiences"
          }), " to apply sync templates when you activate an audience."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/journeys",
            children: "Build a journey"
          }), " to start from a journey template instead of an empty canvas."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
